// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // -----------------------------------------------------
  // FETCH WRAPPERY
  // -----------------------------------------------------
  const fetchMe = async () => {
    const res = await fetch("/api/auth/me", {
      method: "GET",
      credentials: "include",
    });

    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  const fetchLogin = async (username, password) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  const fetchLogout = async () => {
    return fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  };

  // -----------------------------------------------------
  // ENRICH USER (sjednocení tvaru user objektu po /me i po loginu)
  // -----------------------------------------------------
  const enrichUser = async (baseUser) => {
    if (!baseUser) return null;

    const enrichedUser = { ...baseUser };

    // ADMIN – načteme pobočky ze seznamu zaměstnanců
    if (enrichedUser.role === "admin") {
      try {
        const r = await fetch(`/api/employees/${enrichedUser.firma}`, {
          credentials: "include",
        });

        const employees = await r.json();

        // extrahujeme pobočky a sjednotíme FLY
        const codes = [
          ...new Set(
            employees.map((e) => (e.isFlying ? "FLY" : e.pharmacyCode))
          ),
        ].filter(Boolean);

        enrichedUser.availablePharmacies = codes.sort();
      } catch (e) {
        enrichedUser.availablePharmacies = [];
      }
    }

    // RM – předáme regions dál (pokud existuje)
    if (enrichedUser.role === "rm") {
      enrichedUser.availablePharmacies = enrichedUser.regions || [];
    }

    // User – jen jeho pobočka
    if (enrichedUser.role === "user") {
      enrichedUser.availablePharmacies = [enrichedUser.pharmacyCode];
    }

    return enrichedUser;
  };

  // -----------------------------------------------------
  // 1) Načtení uživatele při startu aplikace
  // -----------------------------------------------------
  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      try {
        const { res, data } = await fetchMe();


        if (res.ok && data?.user) {
          const enriched = await enrichUser(data.user);
          setUser(enriched);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error("❌ auth/me error:", err);
        setUser(null);
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  // -----------------------------------------------------
  // 2) LOGIN
  // -----------------------------------------------------
  const login = async (username, password) => {
    setError(null);

    try {
      const { res, data } = await fetchLogin(username, password);

      if (!res.ok) {
        setUser(null);
        setError(data?.message || "Neplatné přihlašovací údaje.");
        return null;
      }

      if (data?.user) {
        // ✅ po loginu si vždy ověř /me (sjednotí to tvar user objektu a role chování)
        try {
          const { res: meRes, data: meData } = await fetchMe();

          if (meRes.ok && meData?.user) {
            const enriched = await enrichUser(meData.user);
            setUser(enriched);
            return enriched;
          }
        } catch (e) {
          // když /me selže, aspoň použijeme login response
        }

        // fallback: původní chování (abychom nic nerozbili)
        const enrichedFallback = await enrichUser(data.user);
        setUser(enrichedFallback);
        return enrichedFallback;
      }
      setError("Neplatná odpověď serveru.");
      setUser(null);
      return null;
    } catch (err) {
      console.error("❌ login error:", err);
      setError("Chyba při přihlášení.");
      setUser(null);
      return null;
    }
  };

  // -----------------------------------------------------
  // 3) LOGOUT
  // -----------------------------------------------------
  const logout = async () => {
    try {
      await fetchLogout();
    } catch (e) {}

    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
